import { MeetingInfo, EmailDraft, Activity } from '../models/types';
import { formatDate, formatEmailDate } from '../utils/dateFormatter';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import {
  generateFraportEmailTemplate
} from '../utils/templateHelpers';
import { v4 as uuidv4 } from 'uuid';

/**
 * Interface for meeting dates used in email templates
 */
interface MeetingDates {
  capexMeetings: Activity[];
  templateDistributionMeetings: Activity[];
  receiveInputMeetings: Activity[];
  trafficDataMeetings: Activity[];
  consultationApprovalMeetings: Activity[];
}

/**
 * Service for generating email templates based on meeting information
 */
export class EmailTemplateService {
  /**
   * Checks if any required meeting types are missing
   * @param meetingDates The meeting dates object containing all categorized meetings
   * @returns Object with information about missing meeting types
   */
  public checkMissingMeetingTypes(meetingDates: MeetingDates): {
    hasMissingTypes: boolean;
    missingTypes: string[];
  } {
    const missingTypes: string[] = [];
    
    if (meetingDates.capexMeetings.length === 0) {
      missingTypes.push('CAPEX meetings');
    }
    
    if (meetingDates.templateDistributionMeetings.length === 0) {
      missingTypes.push('Template distribution meetings');
    }
    
    if (meetingDates.receiveInputMeetings.length === 0) {
      missingTypes.push('Receive input meetings');
    }
    
    if (meetingDates.trafficDataMeetings.length === 0) {
      missingTypes.push('Traffic data meetings');
    }
    
    return {
      hasMissingTypes: missingTypes.length > 0,
      missingTypes
    };
  }
  /**
   * Finds specific types of meetings in the meeting information
   * @param meetingInfo The meeting information containing activities
   * @returns Object with categorized meetings
   */
  public findSpecificMeetings(meetingInfo: MeetingInfo): MeetingDates {
    const capexMeetings: Activity[] = [];
    const templateDistributionMeetings: Activity[] = [];
    const receiveInputMeetings: Activity[] = [];
    const trafficDataMeetings: Activity[] = [];
    const consultationApprovalMeetings: Activity[] = [];
    
    // If no activities, return empty arrays
    if (!meetingInfo.activities || meetingInfo.activities.length === 0) {
      return { 
        capexMeetings, 
        templateDistributionMeetings, 
        receiveInputMeetings, 
        trafficDataMeetings,
        consultationApprovalMeetings 
      };
    }
    
    // Get forecasting process period if available
    const forecastProcess = meetingInfo.forecastProcess;
    const periodStart = forecastProcess ? forecastProcess.startDate : null;
    const periodEnd = forecastProcess && forecastProcess.endDate ? forecastProcess.endDate : null;
        
    // Ensure the main meeting date is within the forecasting period
    if (meetingInfo.meetingDate && forecastProcess) {
      const meetingDate = new Date(meetingInfo.meetingDate);
      const isWithinPeriod = (
        (!periodStart || meetingDate >= periodStart) &&
        (!periodEnd || meetingDate <= periodEnd)
      );
      
      // If meeting date is outside the period, adjust it to be within the period
      if (!isWithinPeriod && periodStart) {
        console.warn('Meeting date is outside the forecasting period, adjusting to use period start date');
        meetingInfo.meetingDate = new Date(periodStart);
      }
    }
    
    // Search through all activities to find specific types
    meetingInfo.activities.forEach(activity => {
      // IMPORTANT: For consultation & approval meetings, we want to consider ALL activities,
      // regardless of the period, because these meetings might be scheduled outside the
      // normal forecasting period
      const isWithinPeriod = true; // Consider all activities for now
            
      // Only process activities within the period
      if (isWithinPeriod) {
        const title = activity.title?.toLowerCase() || '';
        const description = activity.description?.toLowerCase() || '';
        const comments = activity.comments?.toLowerCase() || '';
        
        // Check for CAPEX meetings
        if (
          title.includes('capex')
        ) {
          capexMeetings.push(activity);
        }
        
        // Check for template distribution meetings
        if (
          title.includes('distribute') &&
          title.includes('planner') ||
          description.includes('distribute') &&
          description.includes('planner') ||
          comments.includes('distribute') &&
          comments.includes('planner')
        ) {
          templateDistributionMeetings.push(activity);
        }
        
        // Check for "receive input" meetings
        if (
          title.includes('receive input') ||
          description.includes('receive input') ||
          comments.includes('receive input') ||
          title.includes('input from') ||
          description.includes('input from') ||
          comments.includes('input from')
        ) {
          receiveInputMeetings.push(activity);
        }
        
        // Check for "traffic data" meetings
        if (
          title.includes('traffic data')
        ) {
          trafficDataMeetings.push(activity);
        }
        
        // Check for "consultation & approval" meetings
        // Extract the forecast process name to get the current quarter (e.g., "Q1 2025")
        const forecastName = forecastProcess?.name || '';
        const quarterMatch = forecastName.match(/Q(\d+)/i);
        const currentQuarter = quarterMatch ? quarterMatch[0].toUpperCase() : '';
        
        // Get the original title (not lowercase) for more accurate matching
        const originalTitle = activity.title || '';
        
        // First check if this is a FC Q1 consultation & approval meeting
        // Make the search more flexible to catch variations
        if (currentQuarter && 
            (originalTitle.includes(`FC ${currentQuarter}`) || originalTitle.includes(`FC${currentQuarter}`)) && 
            (originalTitle.toLowerCase().includes('consultation') || originalTitle.toLowerCase().includes('consult')) && 
            originalTitle.toLowerCase().includes('approval')) {
          
          // This is a consultation & approval meeting for the current quarter
          // Add it to the beginning of the array to prioritize it
          consultationApprovalMeetings.unshift(activity);
        } 
        // If no quarter-specific meeting was found, check for any consultation & approval meeting
        else if (
          title.includes('consultation') && title.includes('approval') ||
          description.includes('consultation') && description.includes('approval') ||
          comments.includes('consultation') && comments.includes('approval')
        ) {
          // Add to the end of the array (lower priority)
          consultationApprovalMeetings.push(activity);
        }
      }
    });
    
    return { 
      capexMeetings, 
      templateDistributionMeetings, 
      receiveInputMeetings, 
      trafficDataMeetings,
      consultationApprovalMeetings 
    };
  }
  /**
   * Generates an email draft based on meeting information
   * @param meetingInfo The meeting information
   * @param userId The ID of the user generating the draft
   * @returns An EmailDraft object
   */
  public generateEmailDraft(meetingInfo: MeetingInfo, userId: string): EmailDraft {
    // Find specific meetings for the template
    const specificMeetings = this.findSpecificMeetings(meetingInfo);
  
    const subject = this.generateSubject(meetingInfo);
    const htmlContent = this.createHtmlTemplate(meetingInfo, specificMeetings);
    const textContent = this.createTextTemplate(meetingInfo, specificMeetings);
    
    return {
      id: uuidv4(),
      subject,
      htmlContent,
      textContent,
      meetingInfo,
      createdAt: new Date(),
      createdBy: userId
    };
  }
  
  /**
   * Generates the email subject
   * @param meetingInfo The meeting information
   * @returns The email subject
   */
  private generateSubject(meetingInfo: MeetingInfo): string {
    // Find specific meetings to get the consultation & approval meeting date
    const specificMeetings = this.findSpecificMeetings(meetingInfo);
    
    // Use the same date logic as in the email body
    const meetingDate = specificMeetings.consultationApprovalMeetings.length > 0 
      ? specificMeetings.consultationApprovalMeetings[0].startDate
      : meetingInfo.meetingDate;
    
    return `Invitation: ${meetingInfo.title} - ${formatDate(meetingDate, 'dd.MM.yyyy')}`;
  }
  
  /**
   * Creates an HTML email template
   * @param meetingInfo The meeting information
   * @returns HTML content
   */
  private createHtmlTemplate(meetingInfo: MeetingInfo, specificMeetings: MeetingDates): string {
    // Get forecasting process information
    const forecastProcess = meetingInfo.forecastProcess;
    const forecastName = forecastProcess ? forecastProcess.name : '{FORECAST NAME}';
    const forecastPeriod = forecastProcess
      ? `${format(forecastProcess.startDate, 'MMM yyyy', { locale: enUS })} - ${forecastProcess.endDate ? format(forecastProcess.endDate, 'MMM yyyy', { locale: enUS }) : 'ongoing'}`
      : '{FORECAST PERIOD}';
    
    // Generate CAPEX dates content
    let capexDatesContent = '';
    if (specificMeetings.capexMeetings.length === 0) {
      // No specific CAPEX meetings found, use default date
      capexDatesContent = `By ${formatEmailDate(meetingInfo.meetingDate)}, planners will receive the forms for CAPEX planning.`;
    } else if (specificMeetings.capexMeetings.length === 1) {
      // Single CAPEX meeting
      capexDatesContent = `By ${formatEmailDate(specificMeetings.capexMeetings[0].startDate)}, planners will receive the forms for CAPEX planning.`;
    } else {
      // Multiple CAPEX meetings
      capexDatesContent = `Planners will receive the forms for CAPEX planning on the following dates:
        <ul style="margin-top: 3px; margin-bottom: 3px; padding-left: 12px;">
          ${specificMeetings.capexMeetings.map(meeting =>
            `<li style="margin-bottom: 2px;">${formatEmailDate(meeting.startDate)}${meeting.title ? ` - ${meeting.title}` : ''}</li>`
          ).join('\n')}
        </ul>`;
    }
    
    // Generate template distribution dates content
    let templateDistributionContent = '';
    if (specificMeetings.templateDistributionMeetings.length === 0) {
      // No specific template distribution meetings found, use default date
      templateDistributionContent = `On ${formatEmailDate(meetingInfo.meetingDate)}, controlling will send planners templates.`;
    } else if (specificMeetings.templateDistributionMeetings.length === 1) {
      // Single template distribution meeting
      templateDistributionContent = `On ${formatEmailDate(specificMeetings.templateDistributionMeetings[0].startDate)}, controlling will send planners templates.`;
    } else {
      // Multiple template distribution meetings
      templateDistributionContent = `Controlling will send planners templates on the following dates:
        <ul style="margin-top: 3px; margin-bottom: 3px; padding-left: 12px;">
          ${specificMeetings.templateDistributionMeetings.map(meeting =>
            `<li style="margin-bottom: 2px;">${formatEmailDate(meeting.startDate)}${meeting.title ? ` - ${meeting.title}` : ''}</li>`
          ).join('\n')}
        </ul>`;
    }
    
    const content = `
      <div class="section">
        <p>Dear all,</p>
      </div>
      
      <div class="section">
        <p>The next meeting regarding the forecast process <strong>${forecastName}</strong> (${forecastPeriod}) is approaching and will be held on
        <strong>${specificMeetings.consultationApprovalMeetings.length > 0 
          ? formatEmailDate(specificMeetings.consultationApprovalMeetings[0].startDate)
          : formatEmailDate(meetingInfo.meetingDate)
        }</strong>.</p>
        
        <p>At this meeting, it will be necessary to report on the assessment through the end of ${meetingInfo.meetingDate.getFullYear()}.</p>
      </div>
      
      <div class="section">
        <p>By <strong>${specificMeetings.trafficDataMeetings.length > 0
            ? formatEmailDate(specificMeetings.trafficDataMeetings[0].startDate)
            : '{TRAFFIC_DATA}'
          }</strong>, traffic data for the year ${meetingInfo.meetingDate.getFullYear()} (monthly) must be available.</p>
      </div>
      
      <div class="section">
        <p class="section-title">Implementation plan to prepare ${forecastName}:</p>
        
        <ul>
          <li>${capexDatesContent}</li>
          <li>${templateDistributionContent}</li>
          <li>${specificMeetings.receiveInputMeetings.length > 0
            ? `Please return the completed forms for cost/revenue plan and CAPEX ${forecastName} by ${
                specificMeetings.receiveInputMeetings.length === 1
                  ? `<strong>${formatEmailDate(specificMeetings.receiveInputMeetings[0].startDate)}</strong>`
                  : `the following dates:<ul style="margin-top: 10px; margin-bottom: 10px; padding-left: 20px;">${
                      specificMeetings.receiveInputMeetings.map(meeting =>
                        `<li style="margin-bottom: 8px;">${formatEmailDate(meeting.startDate)}${meeting.title ? ` - ${meeting.title}` : ''}</li>`
                      ).join('\n')
                    }</ul>`
              }`
            : `Please return the completed forms for cost/revenue plan and CAPEX ${forecastName} by {Receive input from responsible planners} and
                {Receive CAPEX input from responsible planners}.`
          }</li>
        </ul>
      </div>
      
      <div class="section">
        <p>Please adhere to these deadlines. We are available for any additional explanations and assistance.</p>
      </div>
      
      <div class="section">
        <p>Best regards,<br/>Andrej Svetina</p>
      </div>
    `;
    
    return generateFraportEmailTemplate(meetingInfo.title, content);
  }
  
  /**
   * Creates a plain text email template
   * @param meetingInfo The meeting information
   * @returns Plain text content
   */
  private createTextTemplate(meetingInfo: MeetingInfo, specificMeetings: MeetingDates): string {
    // Get forecasting process information
    const forecastProcess = meetingInfo.forecastProcess;
    const forecastName = forecastProcess ? forecastProcess.name : '{FORECAST NAME}';
    const forecastPeriod = forecastProcess
      ? `${format(forecastProcess.startDate, 'MMM yyyy', { locale: enUS })} - ${forecastProcess.endDate ? format(forecastProcess.endDate, 'MMM yyyy', { locale: enUS }) : 'ongoing'}`
      : '{FORECAST PERIOD}';
    
    // Generate CAPEX dates content
    let capexDatesContent = '';
    if (specificMeetings.capexMeetings.length === 0) {
      // No specific CAPEX meetings found, use default date
      capexDatesContent = `By ${formatEmailDate(meetingInfo.meetingDate)}, planners will receive the forms for CAPEX planning.`;
    } else if (specificMeetings.capexMeetings.length === 1) {
      // Single CAPEX meeting
      capexDatesContent = `By ${formatEmailDate(specificMeetings.capexMeetings[0].startDate)}, planners will receive the forms for CAPEX planning.`;
    } else {
      // Multiple CAPEX meetings
      capexDatesContent = `Planners will receive the forms for CAPEX planning on the following dates:\n${
        specificMeetings.capexMeetings.map(meeting =>
          `    * ${formatEmailDate(meeting.startDate)}${meeting.title ? ` - ${meeting.title}` : ''}`
        ).join('\n')
      }`;
    }
    
    // Generate template distribution dates content
    let templateDistributionContent = '';
    if (specificMeetings.templateDistributionMeetings.length === 0) {
      // No specific template distribution meetings found, use default date
      templateDistributionContent = `On ${formatEmailDate(meetingInfo.meetingDate)}, controlling will send planners templates.`;
    } else if (specificMeetings.templateDistributionMeetings.length === 1) {
      // Single template distribution meeting
      templateDistributionContent = `On ${formatEmailDate(specificMeetings.templateDistributionMeetings[0].startDate)}, controlling will send planners templates.`;
    } else {
      // Multiple template distribution meetings
      templateDistributionContent = `Controlling will send planners templates on the following dates:\n${
        specificMeetings.templateDistributionMeetings.map(meeting =>
          `    * ${formatEmailDate(meeting.startDate)}${meeting.title ? ` - ${meeting.title}` : ''}`
        ).join('\n')
      }`;
    }
      
return `
INVITATION: ${meetingInfo.title}

Dear all,

The next meeting regarding the forecast process ${forecastName} (${forecastPeriod}) is approaching and will be held on ${
  specificMeetings.consultationApprovalMeetings.length > 0 
    ? formatEmailDate(specificMeetings.consultationApprovalMeetings[0].startDate)
    : formatEmailDate(meetingInfo.meetingDate)
}.
At this meeting, it will be necessary to report on the assessment through the end of ${meetingInfo.meetingDate.getFullYear()}.

By ${specificMeetings.trafficDataMeetings.length > 0
    ? formatEmailDate(specificMeetings.trafficDataMeetings[0].startDate)
    : '{TRAFFIC_DATA}'
  }, traffic data for the year ${meetingInfo.meetingDate.getFullYear()} (monthly) must be available.

Below is the implementation plan to prepare ${forecastName}:

- ${capexDatesContent}
- ${templateDistributionContent}
- ${specificMeetings.receiveInputMeetings.length > 0
    ? `Please return the completed forms for cost/revenue plan and CAPEX ${forecastName} by ${
        specificMeetings.receiveInputMeetings.length === 1
          ? formatEmailDate(specificMeetings.receiveInputMeetings[0].startDate)
          : `the following dates:\n${
              specificMeetings.receiveInputMeetings.map(meeting =>
                `    * ${formatEmailDate(meeting.startDate)}${meeting.title ? ` - ${meeting.title}` : ''}`
              ).join('\n')
            }`
      }`
    : `Please return the completed forms for cost/revenue plan and CAPEX ${forecastName} by {Receive input from responsible planners} and {Receive CAPEX input from responsible planners}.`
  }

Please adhere to these deadlines. We are available for any additional explanations and assistance.

Best regards,
Andrej Svetina
---
This is an automatically generated email. Please do not reply directly to this message.
    `;
  }
}
